import type { ListVendorReturnsInput } from "@nexxt/contracts/logistics";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { logisticsClient } from "../client";

// Server state of the vendor returns (50-api/logistics.md, ADR-227): one factory, the same keys for loaders, screens
// and mutations. What the app caches of one return besides its detail (Backstage: its sync) goes under `of`, so
// whatever reloads the returns reloads it too.

/** The filters of the list input (`listVendorReturnsInput`) the screens set, as the URL holds them. */
export type VendorReturnFilters = Pick<ListVendorReturnsInput, "status" | "location" | "supplier" | "q">;

const PAGE_SIZE = 50;

const keys = {
  all: ["vendor-returns"] as const,
  /** Prefix of every cached list: a write puts the return it gets back in each of them. */
  lists: ["vendor-returns", "list"] as const,
  /** Prefix of what the app caches of one return besides its detail. */
  of: (vendorReturnId: string) => ["vendor-returns", "of", vendorReturnId] as const,
};

export const vendorReturnQueries = {
  ...keys,

  list: (filters: VendorReturnFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        logisticsClient().listVendorReturns({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** With its lines and their prices. */
  detail: (vendorReturnId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", vendorReturnId],
      queryFn: ({ signal }) => logisticsClient().getVendorReturn({ vendorReturnId }, signal),
    }),
};
